/**
 * Deterministic OOS-last sort for collection pages.
 *
 * Shopify has no native "sort by inventory" option, so we do it client-side:
 *   1. Fetch every product in the collection from /collections/<handle>/products.json
 *      (one request returns up to 250 products with availability info).
 *   2. Sort: in-stock products first, OOS last (preserving original order
 *      within each group).
 *   3. Determine which product handles belong on the current page based on
 *      that global sort and the page size taken from the rendered grid.
 *   4. If the grid already matches, do nothing.
 *   5. Otherwise walk the collection's natural pages to harvest the rendered
 *      card HTML for the handles we need, then swap the grid contents.
 *
 * Same URL always shows the same products — no borrowing/un-hiding that would
 * make the page contents change between visits.
 */
(function() {
  'use strict';

  if (!document.body.classList.contains('template-collection')) return;

  init();

  async function init() {
    var grid = document.getElementById('main-collection-product-grid');
    if (!grid) return;

    var handle = getCollectionHandle();
    if (!handle) return;

    try {
      var allProducts = await fetchAllProductMeta(handle);
      if (allProducts.length === 0) return;

      var sortedHandles = sortByAvailability(allProducts);

      var fetchResult = await fetchAllCards(handle);
      if (fetchResult.cardsByHandle.size === 0) return;

      // canonicalPerPage comes from page 1's rendered count; falls back to
      // the current grid's count (only wrong on the last page).
      var perPage = fetchResult.canonicalPerPage || countRenderedProducts(grid) || 16;
      var currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
      var start = (currentPage - 1) * perPage;
      var targetHandles = sortedHandles.slice(start, start + perPage);

      var currentHandles = getRenderedHandles(grid);
      if (handlesMatch(currentHandles, targetHandles)) {
        hideTrailingBanners(grid);
        return;
      }

      replaceGridProducts(grid, targetHandles, fetchResult.cardsByHandle);
      hideTrailingBanners(grid);
    } catch (e) {
      console.warn('[oos-sort]', e);
    }
  }

  function getCollectionHandle() {
    var m = window.location.pathname.match(/\/collections\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  async function fetchWithRetry(url, retries) {
    var attempt = 0;
    var max = retries == null ? 2 : retries;
    while (attempt <= max) {
      try {
        var response = await fetch(url, { credentials: 'same-origin' });
        if (response.ok) return response;
      } catch (e) {
        // network error — fall through to retry
      }
      attempt++;
      if (attempt > max) return null;
      // small backoff so a flaky network has time to recover
      await new Promise(function(r) { setTimeout(r, 200 * attempt); });
    }
    return null;
  }

  async function fetchAllProductMeta(handle) {
    var all = [];
    var page = 1;
    var maxPages = 5;

    while (page <= maxPages) {
      var url = '/collections/' + handle + '/products.json?limit=250&page=' + page;
      var response = await fetchWithRetry(url);
      if (!response) break;

      var data = await response.json();
      if (!data.products || data.products.length === 0) break;

      data.products.forEach(function(p) {
        var available = (p.variants || []).some(function(v) { return v.available; });
        all.push({ handle: p.handle, available: available });
      });

      if (data.products.length < 250) break;
      page++;
    }

    return all;
  }

  function sortByAvailability(products) {
    var inStock = [];
    var oos = [];
    products.forEach(function(p) {
      if (p.available) inStock.push(p.handle);
      else oos.push(p.handle);
    });
    return inStock.concat(oos);
  }

  function countRenderedProducts(grid) {
    return grid.querySelectorAll(
      '.product-item-wrapper:not(.collection-full-width-section)'
    ).length;
  }

  function getRenderedHandles(grid) {
    var handles = [];
    grid.querySelectorAll('.product-item-wrapper:not(.collection-full-width-section)').forEach(function(wrapper) {
      var link = wrapper.querySelector('a[href*="/products/"]');
      if (!link) return;
      var m = link.getAttribute('href').match(/\/products\/([^\/?#]+)/);
      if (m) handles.push(m[1]);
    });
    return handles;
  }

  function handlesMatch(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  async function fetchAllCards(handle) {
    var cardsByHandle = new Map();
    var canonicalPerPage = 0;
    var page = 1;
    var maxPages = 20;

    while (page <= maxPages) {
      var url = '/collections/' + handle + '?page=' + page;
      var response = await fetchWithRetry(url);
      if (!response) break;

      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var sourceGrid = doc.getElementById('main-collection-product-grid');
      if (!sourceGrid) break;

      var items = sourceGrid.querySelectorAll(
        '.product-item-wrapper:not(.collection-full-width-section)'
      );
      if (items.length === 0) break;

      if (page === 1) canonicalPerPage = items.length;

      items.forEach(function(item) {
        var link = item.querySelector('a[href*="/products/"]');
        if (!link) return;
        var m = link.getAttribute('href').match(/\/products\/([^\/?#]+)/);
        if (!m) return;
        if (!cardsByHandle.has(m[1])) {
          cardsByHandle.set(m[1], item);
        }
      });

      var nextItem = doc.querySelector('.pagination .next');
      var hasNext = nextItem && !nextItem.classList.contains('disabled') && nextItem.querySelector('a');
      if (!hasNext) break;
      page++;
    }

    return { cardsByHandle: cardsByHandle, canonicalPerPage: canonicalPerPage };
  }

  function replaceGridProducts(grid, targetHandles, cardsByHandle) {
    // Swap each product card in-place at its existing slot so that the
    // full-width USP banners (.collection-full-width-section) interspersed
    // between products keep their original positions (theme renders them
    // after specific product indices to produce the "after row 1, then after
    // every 2 rows" pattern).
    var existingCards = Array.from(grid.querySelectorAll(
      '.product-item-wrapper:not(.collection-full-width-section)'
    ));
    var paginationAnchor = findPaginationContainer(grid);

    var i;
    for (i = 0; i < targetHandles.length; i++) {
      var card = cardsByHandle.get(targetHandles[i]);
      if (!card) continue;
      var clone = card.cloneNode(true);

      if (i < existingCards.length) {
        // Replace in place — keeps interspersed full-width banners undisturbed.
        existingCards[i].parentNode.replaceChild(clone, existingCards[i]);
      } else if (paginationAnchor) {
        grid.insertBefore(clone, paginationAnchor);
      } else {
        grid.appendChild(clone);
      }
    }

    // Remove any leftover original cards if the new slice is shorter than the
    // natural page (shouldn't normally happen, but be safe).
    for (i = targetHandles.length; i < existingCards.length; i++) {
      if (existingCards[i].parentNode) existingCards[i].remove();
    }
  }

  function findPaginationContainer(grid) {
    var pagination = grid.querySelector('.pagination');
    if (!pagination) return null;
    var node = pagination;
    while (node && node.parentNode !== grid) {
      node = node.parentNode;
    }
    return node && node.parentNode === grid ? node : null;
  }

  function findFirstFullWidthSection(grid) {
    return grid.querySelector('.product-item-wrapper.collection-full-width-section');
  }

  // Hide any full-width USP banner that no longer has product cards following
  // it — happens on the last page when there are too few products to follow up.
  function hideTrailingBanners(grid) {
    var children = Array.from(grid.children);
    var sawCardAfter = false;
    for (var i = children.length - 1; i >= 0; i--) {
      var c = children[i];
      var isWrapper = c.classList && c.classList.contains('product-item-wrapper');
      if (!isWrapper) continue;
      var isBanner = c.classList.contains('collection-full-width-section');
      if (!isBanner) {
        sawCardAfter = true;
      } else {
        if (!sawCardAfter) {
          c.style.display = 'none';
        } else {
          // Once we've seen a card after this banner, all earlier banners are
          // followed by at least that card too — no need to keep checking.
          break;
        }
      }
    }
  }
})();
